import React, { useContext, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import PropTypes from 'prop-types';
import { useMutation } from '@apollo/react-hooks';
import submissionResponse from '../../apollo/mutations/submissionResponse.graphql';
import { percentage } from '../../utils/unit-converter';
import { afterSubmissionResponseReturn } from '../../helpers/afterSubmissionResponseReturn';
import { useLanguage } from '../../utils/language';
import handleGraphQLMutationErrors from '../../helpers/handleGraphQLMutationErrors';
import AssignmentContext from '../common/AssignmentContext/AssignmentContext';
import fireNotification from '../../utils/fireNotification';
import {
  classAssignmentListQuery,
  classAssignmentSubmissionPageQuery,
} from '../AssignmentSubmissionEvaluate/constants';
import {
  GradeBoxWrapper,
  NewGradeScoreWrapper,
  PercentageBubble,
  ReturnedScore,
} from './studentListStyles';

export const GradeScore = ({
  maxScore,
  isReturned,
  onMutationCompleted,
  isSubmitted,
  student,
  submission,
  response,
  isGraded,
  className,
  deferredSave,
  isFocused,
  setFocus,
  attachmentRequired,
  commentRequired,
  onFocus,
  onBlur,
  isActiveStudentInTheClass,
  isStudentSubmission,
}) => {
  const { t } = useTranslation();
  const {
    assignmentParams: {
      assignmentInstanceId,
      mloId,
      classId,
      instructionalPlanId,
      contentId,
    },
    assignmentData,
  } = useContext(AssignmentContext);

  // ToDo:i18n cleanup with i18n.
  const { langCode } = useLanguage();
  const refetchQueryName = isStudentSubmission
    ? classAssignmentSubmissionPageQuery
    : classAssignmentListQuery;
  const [submissionResponseGradeMutation] = useMutation(submissionResponse, {
    onError: error =>
      handleGraphQLMutationErrors(error, 'NetworkError', langCode),
    refetchQueries: [refetchQueryName],
  });
  const score = response && response.score;
  const [isGradeEditable, setGradeEditable] = useState(isFocused);
  const [prevScoreValue, setPrevScoreValue] = useState(score);
  const [isExceedMaxScore, setIsExceedMaxScore] = useState(score > maxScore);
  const gradeInputElm = useRef(null);
  const onEditGrade = e => {
    if (!isReturned && isActiveStudentInTheClass) {
      e.stopPropagation();
      setPrevScoreValue(score);
    }
    setGradeEditable(!isReturned && isActiveStudentInTheClass);
    setFocus(!isReturned && isActiveStudentInTheClass);
  };
  const onGradeInputFocus = e => {
    onEditGrade(e);
    onFocus(e);
  };

  const onGradeInputBlur = (studentSubmission, studentName) => async e => {
    const hasInputValue = gradeInputElm.current && gradeInputElm.current.value;
    if (hasInputValue && Number(gradeInputElm.current.value) > maxScore) {
      setIsExceedMaxScore(true);
      fireNotification(
        'evaluatedExceedTheMaxScoreError',
        langCode,
        t("The student's score cannot exceed the max score."),
      );
      return;
    }
    setIsExceedMaxScore(false);
    if (hasInputValue && score === Number(gradeInputElm.current.value)) {
      setGradeEditable(false);
      setFocus(false);
      return;
    }

    const inputValue = hasInputValue
      ? Number(gradeInputElm.current.value)
      : null;

    onBlur(e);

    if (deferredSave) {
      onMutationCompleted(inputValue);
      setGradeEditable(false);
      setFocus(false);
    } else {
      await submissionResponseGradeMutation({
        variables: {
          id: studentSubmission ? studentSubmission.id : undefined,
          submissionTeacherInput: !studentSubmission
            ? {
                assignmentId: assignmentData.assignmentId,
                studentId: student.id,
                referrerId: assignmentInstanceId || mloId,
                instructionalPlanId,
                mloId,
                contentId,
                classId,
              }
            : undefined,
          input: {
            score: hasInputValue ? Number(gradeInputElm.current.value) : null,
            comment: response && response.comment,
            attachment:
              response && response.attachment
                ? {
                    fileId: response.attachment.fileId,
                    fileName: response.attachment.fileName,
                    path: response.attachment.path,
                  }
                : null,
          },
        },
        update: (cache, { data }) => {
          setGradeEditable(false);
          setFocus(false);
          afterSubmissionResponseReturn({
            isReturning: false,
            commentRequired,
            attachmentRequired,
          })(cache, { data });
        },
      });
    }

    if (hasInputValue && !deferredSave) {
      fireNotification(
        'evaluatedStudentByName',
        langCode,
        t('{{STUDENT_NAME}} assignment has been evaluated', {
          STUDENT_NAME: studentName,
        }),
      );
    } else if (prevScoreValue && !deferredSave) {
      fireNotification(
        'unEvaluatedStudentByName',
        langCode,
        t('{{STUDENT_NAME}} assignment evaluation has been removed', {
          STUDENT_NAME: studentName,
        }),
      );
    }
  };

  const sanitizeInputValue = () => {
    gradeInputElm.current.value = gradeInputElm.current.value
      .match(/^[0-9]*/g)
      .join('');
  };

  const onKeyUp = e => {
    sanitizeInputValue();
    if (e.key === 'Enter') {
      // Blurring the input will trigger the mutation via the onBlur event handler.
      // It is also a way to provide user feedback that pressing enter actually doing some process
      gradeInputElm.current.blur();
    }
  };

  const canDisableGrade =
    (attachmentRequired || commentRequired) && !isSubmitted;

  const showScoreWithPercentage =
    (deferredSave || isGraded) &&
    Number.isInteger(score) &&
    ((deferredSave && !isFocused) || !isGradeEditable);
  if (showScoreWithPercentage) {
    return (
      <NewGradeScoreWrapper
        className="grade-score-wrapper"
        data-testid={`graded-score-${student.id}`}
        onClick={onEditGrade}
        isToBeReturned={isGraded && !isReturned}
      >
        {isReturned ? (
          <ReturnedScore>{score}</ReturnedScore>
        ) : (
          <GradeBoxWrapper
            onClick={e => e.stopPropagation()}
            className={className}
            isExceedMaxScore={isExceedMaxScore}
          >
            {isActiveStudentInTheClass ? (
              <input
                data-testid={`grade-score-input-${student.id}`}
                type="text"
                disabled={canDisableGrade}
                placeholder="--"
                ref={gradeInputElm}
                onFocus={onGradeInputFocus}
                onClick={e => e.stopPropagation()}
                autoFocus={
                  isGraded && ((deferredSave && isFocused) || isGradeEditable)
                }
                onKeyUp={onKeyUp}
                defaultValue={score}
                onBlur={onGradeInputBlur(submission, student.name)}
              />
            ) : (
              <span data-testid="grade-max-score" className="max-score">
                --
              </span>
            )}
            <span data-testid="grade-max-score-separator" className="separator">
              /
            </span>
            <span data-testid="grade-max-score" className="max-score">
              {maxScore}
            </span>
          </GradeBoxWrapper>
        )}
        <PercentageBubble
          isBelowTreshold={percentage(score, maxScore) < 60}
          isExceedMaxScore={isExceedMaxScore}
        >
          {percentage(score, maxScore)}%
        </PercentageBubble>
      </NewGradeScoreWrapper>
    );
  }
  return (
    <GradeBoxWrapper
      onClick={e => e.stopPropagation()}
      className={className}
      isExceedMaxScore={isExceedMaxScore}
    >
      {isActiveStudentInTheClass ? (
        <input
          data-testid={`grade-score-input-${student.id}`}
          type="text"
          disabled={canDisableGrade}
          placeholder="--"
          ref={gradeInputElm}
          onFocus={onGradeInputFocus}
          onClick={e => e.stopPropagation()}
          autoFocus={
            isGraded && ((deferredSave && isFocused) || isGradeEditable)
          }
          onKeyUp={onKeyUp}
          defaultValue={score}
          onBlur={onGradeInputBlur(submission, student.name)}
        />
      ) : (
        <span data-testid="grade-max-score" className="max-score">
          --
        </span>
      )}
      <span data-testid="grade-max-score-separator" className="separator">
        /
      </span>
      <span data-testid="grade-max-score" className="max-score">
        {maxScore}
      </span>
    </GradeBoxWrapper>
  );
};

GradeScore.defaultProps = {
  submission: null,
  isSubmitted: false,
  isReturned: false,
  isGraded: false,
  response: null,
  className: '',
  deferredSave: false,
  isFocused: false,
  setFocus: () => {},
  onMutationCompleted: null,
  classId: undefined,
  isActiveStudentInTheClass: true,
  onBlur: () => {},
  onFocus: () => {},
  isStudentSubmission: false,
};

GradeScore.propTypes = {
  maxScore: PropTypes.number.isRequired,
  isReturned: PropTypes.bool.isRequired,
  onMutationCompleted: PropTypes.func,
  isSubmitted: PropTypes.bool.isRequired,
  isGraded: PropTypes.bool.isRequired,
  student: PropTypes.object.isRequired,
  submission: PropTypes.object,
  response: PropTypes.object,
  className: PropTypes.string,
  deferredSave: PropTypes.bool,
  isFocused: PropTypes.bool,
  isActiveStudentInTheClass: PropTypes.bool,
  setFocus: PropTypes.func,
  attachmentRequired: PropTypes.bool.isRequired,
  commentRequired: PropTypes.bool.isRequired,
  onFocus: PropTypes.func,
  onBlur: PropTypes.func,
  isStudentSubmission: PropTypes.bool,
};
